/**
 * About — The Figure Co., in the shared PublicShell + the homepage court-exhibit
 * grammar (rules not fills, DM Serif headings, DM Mono meta, Outfit body, squared
 * CTA). Pre-launch: no social proof; stats are grounded in the defensibility
 * model; the architecture is described as documented/reproducible, NOT a public
 * map (that system is confidential / NDA-only).
 */

import React, { useEffect } from 'react';
import PublicShell from '../components/marketing/PublicShell';
import { PrimaryCTA, SectionLink } from '../components/home/sectionPrimitives';

const STATS: Array<{ figure: string; label: string }> = [
  { figure: '44', label: 'Court-ready documents' },
  { figure: '100+', label: 'Industry indices (BLS)' },
  { figure: '0', label: 'Dollar figures invented' },
  { figure: '100%', label: 'Traced to source' },
];

const VALUES: Array<{ n: string; title: string; body: string }> = [
  { n: '01', title: 'Grounded', body: 'Every dollar figure is computed by a deterministic engine and carries a run ID — never an AI guess.' },
  { n: '02', title: 'Defensible', body: 'One number per claim, traceable to its source document. Built to hold up in a deposition.' },
  { n: '03', title: 'Human-attested', body: 'Your accountant approves every engine; your attorney locks and stamps the sworn figure.' },
  { n: '04', title: 'Reproducible', body: 'Every engine is versioned and every run reproducible — the method stands up to an opposing expert.' },
];

const WHAT_WE_DO: Array<{ title: string; body: string }> = [
  { title: 'Engine-grounded BI calculations', body: 'A DAG of deterministic engines reconstructs the loss — bottom-up and top-down — and foots to a single sworn net claim, with first-party property and extended indemnity on their own ledgers.' },
  { title: 'Document intelligence', body: 'We classify and extract from financial statements, tax returns, leases and carrier correspondence — then route each figure to the engine that needs it.' },
  { title: 'Sensitivity & Monte Carlo', body: 'Risk analysis bounds the claim with range projections and statistical confidence, capped at the supported net — never an overshoot.' },
  { title: 'Court-ready deliverables', body: 'Sworn Proof of Loss, demand letters, expert reports, forensic workbooks, Bates-stamped exhibits — each figure traceable to its source.' },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PublicShell wide>
      {/* Hero */}
      <section className="mx-auto max-w-3xl px-5 pb-10 pt-6 text-center sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-text-dim">About</p>
        <h1 className="mt-5 font-heading text-4xl leading-tight tracking-tight text-text-primary md:text-5xl">
          The number that holds up in court.
        </h1>
        <p className="mt-5 font-body text-lg leading-relaxed text-text-secondary">
          Forensic business-interruption claim calculations for accountants, attorneys, and the people
          who have to defend the number — every figure computed by a deterministic engine, reviewed by
          a named human, traceable to its source.
        </p>
      </section>

      <div className="mx-auto max-w-[1100px] px-5 py-12 sm:px-8 lg:py-16">
        {/* Mission + stats */}
        <section className="mb-20 grid items-center gap-12 md:grid-cols-2 md:gap-16">
          <div>
            <h2 className="font-heading text-3xl tracking-tight text-text-primary">Our mission</h2>
            <p className="mt-5 font-body text-base leading-relaxed text-text-secondary lg:text-lg">
              Business-interruption claims are complex, high-stakes, and unforgiving of error. A single
              invented or stale number can lose a case under cross-examination.
            </p>
            <p className="mt-4 font-body text-base leading-relaxed text-text-secondary lg:text-lg">
              We built a platform where the math is deterministic — computed in code, never guessed by a
              language model — and every dollar figure traces to the document it came from. The AI
              interprets; the engines calculate.
            </p>
            <p className="mt-4 font-body text-base leading-relaxed text-text-secondary lg:text-lg">
              The result is a number a plaintiff attorney or CPA can defend line by line, and the
              court-ready documents to assert it.
            </p>
          </div>
          {/* Ruled stat grid — no fill, hairline dividers carry the structure.
              Hueless except the ONE decisive figure (100% traced), matching the
              orange budget of the hero + the auth/waitlist registers (K3). */}
          <dl className="grid grid-cols-2 border-l border-t border-hairline">
            {STATS.map((s) => (
              <div key={s.label} className="border-b border-r border-hairline px-6 py-8">
                <dt
                  className={`font-heading text-4xl leading-none tracking-tight ${
                    s.figure === '100%' ? 'text-brand-text' : 'text-text-primary'
                  }`}
                >
                  {s.figure}
                </dt>
                <dd className="mt-2 font-body text-sm leading-snug text-text-secondary">{s.label}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Values — ruled numbered cells, no icon chips */}
        <section className="mb-20">
          <h2 className="mb-10 font-heading text-3xl tracking-tight text-text-primary">What we stand on</h2>
          <div className="grid gap-8 md:grid-cols-4">
            {VALUES.map((v) => (
              <div key={v.n} className="border-t border-hairline pt-5">
                <span className="font-mono text-[11px] text-text-dim">{v.n}</span>
                <h3 className="mt-3 font-heading text-lg leading-snug tracking-tight text-text-primary">{v.title}</h3>
                <p className="mt-2.5 font-body text-sm leading-relaxed text-text-secondary">{v.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* What we do — ruled ledger, no fill */}
        <section className="mb-20">
          <h2 className="mb-10 font-heading text-3xl tracking-tight text-text-primary">What we do</h2>
          <div className="grid gap-x-12 gap-y-10 border-t border-hairline pt-10 md:grid-cols-2">
            {WHAT_WE_DO.map((w) => (
              <div key={w.title}>
                <h3 className="font-heading text-xl leading-snug tracking-tight text-text-primary">{w.title}</h3>
                <p className="mt-3 font-body leading-relaxed text-text-secondary">{w.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Who's behind it — ruled, no fill */}
        <section className="mb-20">
          <h2 className="mb-8 font-heading text-3xl tracking-tight text-text-primary">Who’s behind it</h2>
          <div className="max-w-2xl border-l-2 border-hairline pl-6">
            <p className="font-body text-base leading-relaxed text-text-secondary lg:text-lg">
              The Figure Co. is built by — and for — forensic practitioners. Ahead of launch we’re
              assembling a team of CPAs and testifying experts to stand behind every number the platform
              produces — because a forensic claim is only as defensible as the people who sign it.
            </p>
          </div>
        </section>

        {/* Closing CTA — squared, matches the homepage */}
        <section className="border-t border-hairline pt-12">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="font-heading text-2xl tracking-tight text-text-primary md:text-3xl">
                Be among the first firms on the platform.
              </h2>
              <p className="mt-2 font-body text-sm text-text-secondary">
                We’re onboarding a small group of plaintiff firms and CPAs in private beta.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-6">
              <PrimaryCTA to="/waitlist">Request early access</PrimaryCTA>
              <SectionLink to="/contact">Contact us</SectionLink>
            </div>
          </div>
        </section>
      </div>
    </PublicShell>
  );
}
